import { View, Text, styled } from 'tamagui'
import { X } from '@tamagui/lucide-icons'
import { useAppStore } from '@/store'

const Overlay = styled(View, {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '$overlay',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 6000,
})

const Modal = styled(View, {
  width: 520,
  maxHeight: '80vh',
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  overflow: 'hidden',
  flexDirection: 'column',
})

const ModalHeader = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingHorizontal: 20,
  paddingVertical: 16,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const ModalTitle = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 18,
  color: '$surfaceForeground',
})

const CloseBtn = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const ModalBody = styled(View, {
  padding: 20,
  gap: 20,
  overflow: 'auto' as any,
})

const Section = styled(View, {
  gap: 8,
})

const SectionTitle = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '600',
  color: '$color8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
})

const ShortcutRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingVertical: 6,
})

const ShortcutLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  color: '$surfaceForeground',
})

const KeyCombo = styled(View, {
  flexDirection: 'row',
  gap: 4,
})

const Key = styled(View, {
  paddingHorizontal: 8,
  paddingVertical: 3,
  borderRadius: '$borderRadiusSm',
  borderWidth: 1,
  borderColor: '$borderDefault',
  backgroundColor: '$sidebar',
  minWidth: 28,
  alignItems: 'center',
})

const KeyText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 11,
  color: '$sidebarMuted',
  fontWeight: '500',
})

const shortcuts = {
  Navigation: [
    { label: 'Command Palette', keys: ['⌘', 'K'] },
    { label: 'Search', keys: ['⌘', 'F'] },
    { label: 'Toggle Members', keys: ['⌘', '⇧', 'M'] },
    { label: 'Close Panel', keys: ['Esc'] },
    { label: 'This Modal', keys: ['?'] },
  ],
  Messages: [
    { label: 'Send Message', keys: ['Enter'] },
    { label: 'New Line', keys: ['⇧', 'Enter'] },
    { label: 'Edit Last Message', keys: ['↑'] },
  ],
  Voice: [
    { label: 'Toggle Mute', keys: ['⌘', '⇧', 'M'] },
    { label: 'Connection Demo', keys: ['⌃', '⇧', 'D'] },
  ],
}

export function KeyboardShortcutsModal() {
  const { setKeyboardShortcutsOpen } = useAppStore()

  return (
    <Overlay onPress={() => setKeyboardShortcutsOpen(false)}>
      <Modal onPress={(e: { stopPropagation: () => void }) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Keyboard Shortcuts</ModalTitle>
          <CloseBtn onPress={() => setKeyboardShortcutsOpen(false)}>
            <X size={16} color="$sidebarMuted" />
          </CloseBtn>
        </ModalHeader>
        <ModalBody>
          {Object.entries(shortcuts).map(([section, items]) => (
            <Section key={section}>
              <SectionTitle>{section}</SectionTitle>
              {items.map((shortcut) => (
                <ShortcutRow key={shortcut.label}>
                  <ShortcutLabel>{shortcut.label}</ShortcutLabel>
                  <KeyCombo>
                    {shortcut.keys.map((key, i) => (
                      <Key key={i}>
                        <KeyText>{key}</KeyText>
                      </Key>
                    ))}
                  </KeyCombo>
                </ShortcutRow>
              ))}
            </Section>
          ))}
        </ModalBody>
      </Modal>
    </Overlay>
  )
}
